import type { Guest } from '../types/ui/Guests';

type HonoreeGuest = Pick<Guest, 'name' | 'is_honoree' | 'archived' | 'role'>;

const clean = (v?: string | null) => (v ?? '').trim();

/**
 * The couple, derived only from the project's honoree guests (the people the
 * event celebrates), in a stable order (role first, a missing role last, then name) so the line does not
 * shuffle between loads. Wording is agnostic: no bride/groom assumption.
 * Settings no longer hold couple names.
 */
export function honoreeNames(guests: ReadonlyArray<HonoreeGuest> | undefined): string[] {
  return (guests ?? [])
    .filter(x => x.is_honoree && !x.archived && clean(x.name))
    .map(x => ({ name: clean(x.name), role: clean(x.role) }))
    .sort((a, b) => Number(!a.role) - Number(!b.role) || a.role.localeCompare(b.role) || a.name.localeCompare(b.name))
    .map(x => x.name);
}

/**
 * "Amy & Zed", "Amy", or '' when there are no honorees. Shows at most
 * `maxShown` names then "+N", so a long list stays on one line on a phone.
 */
export function coupleLine(guests: ReadonlyArray<HonoreeGuest> | undefined, maxShown = 3): string {
  const all = honoreeNames(guests);
  if (all.length <= maxShown) return all.join(' & ');
  return `${all.slice(0, maxShown).join(' & ')} +${all.length - maxShown}`;
}
